import { Loading } from '@teko/ds-desktop';
import { FilterOption } from '@teko/types/dist/ecommerce';
import { cleanObject } from '@teko/utils';
import { DEFAULT_PRICE_FILTER, HAMPER_PAGE_SIZE } from 'constants/index';
import { convertDiscoveryFilter, convertToPayloadSorting } from 'helpers';
import { convertFilter, getDisplayFilter } from 'helpers/buildpc';
import { useHamperProducts } from 'hooks';
import { t } from 'i18n';
import {
  cookFilterPrice,
  extractSortQuery,
} from 'modules/search-page/shared/helpers';
import { SortOptionInterface } from 'modules/search-page/shared/types';
import { useEffect, useMemo, useState } from 'react';
import { ProductSelectModalComponent } from '../../BuildPC/BuildPcModal';

type HamperModalProps = {
  collectionSlug: string;
  visible: boolean;
  hamperItemKey: string;
  onClose: () => void;
};

export const HamperModal: React.FC<HamperModalProps> = (props) => {
  const { visible, onClose, collectionSlug, hamperItemKey } = props;
  const [priceFilterValue, setPriceFilterValue] = useState<{
    gte: number;
    lte: number;
  }>();
  const [keyword, setKeyword] = useState('');
  const [activeOptions, setActiveOptions] = useState<FilterOption[]>([]);
  const [activeSortOption, setActiveSortOption] = useState<
    SortOptionInterface | undefined
  >();
  const [currentPage, setCurrentPage] = useState(1);

  useEffect(() => {
    setCurrentPage(1);
  }, [keyword]);

  const [sort, order] = activeSortOption
    ? extractSortQuery(activeSortOption.optionCode)
    : [undefined, undefined];

  // fetch filter block data
  const { filter, loading: filterLoading } = useHamperProducts({
    slug: collectionSlug,
    returnFilterable: [
      'FILTER_TYPE_ATTRIBUTE',
      'FILTER_TYPE_BRAND',
      'FILTER_TYPE_PRICE',
      'FILTER_TYPE_CLEARANCE',
    ],
    pageSize: 1,
  });

  // fetch product list
  const {
    products,
    pagination,
    loading: productLoading,
  } = useHamperProducts(
    {
      slug: collectionSlug,
      query: keyword,
      sorting: convertToPayloadSorting(sort, order),
      filter: cleanObject({
        ...convertFilter(activeOptions),
        priceGte: priceFilterValue?.gte,
        priceLte: priceFilterValue?.lte,
      }),
      pageSize: HAMPER_PAGE_SIZE,
      page: currentPage,
    },
    hamperItemKey,
  );

  const filters = useMemo(() => {
    return !!filter ? convertDiscoveryFilter(filter) : [];
  }, [filter]);

  const displayFilters = useMemo(() => {
    return getDisplayFilter(filters);
  }, [filters]);

  const priceFilter = useMemo(() => {
    return cookFilterPrice(filter?.priceLte, filter?.priceGte);
  }, [filter?.priceLte, filter?.priceGte]);

  const onSortOptionSelected = (sortOption: SortOptionInterface) => {
    if (sortOption.optionCode === activeSortOption?.optionCode) {
      setActiveSortOption(undefined);
    } else {
      setActiveSortOption(sortOption);
    }
  };

  const onClickFilterOption = (option: FilterOption) => {
    const optionIndex = activeOptions.findIndex(
      (opt) =>
        opt.filterCode === option.filterCode &&
        opt.filterId === option.filterId &&
        opt.optionId === option.optionId,
    );
    if (optionIndex > -1) {
      setActiveOptions((prev) =>
        prev.filter((_, index) => index !== optionIndex),
      );
    } else {
      setActiveOptions((prev) => [...prev, option]);
    }
  };

  const onClickSearch = (keyword: string) => {
    setKeyword(keyword);
  };

  return (
    <Loading isLoading={filterLoading || productLoading}>
      <ProductSelectModalComponent
        visible={visible}
        onClose={() => {
          onClose?.();
          setActiveOptions([]);
          setPriceFilterValue(undefined);
          setActiveSortOption(undefined);
        }}
        listProducts={products}
        filters={displayFilters}
        pagination={pagination || { totalItems: 0, totalPages: 0 }}
        filterPrice={{
          ...priceFilter,
          value: priceFilterValue || DEFAULT_PRICE_FILTER,
        }}
        currentPage={currentPage}
        onSortByPrice={(gte: number, lte: number) =>
          setPriceFilterValue({ gte, lte })
        }
        onPageChange={(page) => setCurrentPage(page)}
        activeSortOption={activeSortOption}
        activeOptions={activeOptions}
        onSortOptionSelected={onSortOptionSelected}
        onClearFilter={() => setActiveOptions([])}
        onClickFilterOption={onClickFilterOption}
        onClickSearch={onClickSearch}
        isLoading={filterLoading || productLoading}
        title={t('Chọn sản phẩm')}
        isUsingHamper={true}
        hamperItemKey={hamperItemKey}
      />
    </Loading>
  );
};
